import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import { headerCellStyle, bodyCellStyle } from "./components/SelectableEnrollmentTable";

interface TagType {
  id: number;
  name: string;
  tags: Array<{ id: number; name: string }>;
}

interface BulkAssignTagsModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  tagTypes: TagType[];
  onSuccess: () => void;
}

interface ResultRow {
  enrollmentId: number;
  status: "tagged" | "skipped" | "not_found" | "error";
  reason?: string;
  error?: string;
}

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    tagged:    { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:   { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found: { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:     { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const BulkAssignTagsModal: React.FC<BulkAssignTagsModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  tagTypes,
  onSuccess,
}) => {
  const [tagTypeId, setTagTypeId] = useState<number | "">("");
  const [tagId, setTagId] = useState<number | "">("");
  const [taggedDate, setTaggedDate] = useState<string>(todayStr());
  const [remarks, setRemarks] = useState("");
  // Current active tag of the chosen type per enrollment (legacy "Existing Tag").
  const [existing, setExisting] = useState<Record<number, { tagName: string; taggedDate: string | null }>>({});

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  const { enrollments, selectedIds, selectedEnrollmentIds, toggle, toggleAll, allSelected } =
    useEnrollmentSelection(selectedEnquiryIds, enquiryEnrollmentsMap, studentList, show);

  const tagsForType = useMemo(() => {
    if (!tagTypeId) return [];
    return tagTypes.find((t) => t.id === tagTypeId)?.tags || [];
  }, [tagTypes, tagTypeId]);

  // Reset the selected tag whenever the tag type changes.
  useEffect(() => {
    setTagId("");
  }, [tagTypeId]);

  // Fetch each enrollment's current tag of the chosen type (Existing Tag column).
  useEffect(() => {
    if (!show) return;
    if (!tagTypeId || enrollments.length === 0) {
      setExisting({});
      return;
    }
    let cancelled = false;
    EnrollmentService.getExistingTags({
      enrollmentIds: enrollments.map((e) => e.enrollmentId),
      tagTypeId: Number(tagTypeId),
    })
      .then((resp) => {
        if (!cancelled) setExisting(resp.data?.data?.existing || {});
      })
      .catch(() => {
        if (!cancelled) setExisting({});
      });
    return () => {
      cancelled = true;
    };
  }, [show, tagTypeId, enrollments]);

  const handleClose = () => {
    setTagTypeId("");
    setTagId("");
    setTaggedDate(todayStr());
    setRemarks("");
    setExisting({});
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!tagTypeId) return setError("Pick a tag type.");
    if (!tagId) return setError("Pick a tag.");
    if (!taggedDate) return setError("Tag Date is required.");
    if (selectedEnrollmentIds.length === 0) {
      return setError("Select at least one enrollment.");
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkAssignTags({
        enrollmentIds: selectedEnrollmentIds,
        tagTypeId: Number(tagTypeId),
        tagId: Number(tagId),
        taggedDate: taggedDate || null,
        remarks: remarks.trim() || null,
      });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Bulk assign tags failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results ? "Bulk Update Student Tags — Results" : "Bulk Update Student Tags"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Form.Group className="mb-3" style={{ flex: "1 1 220px" }}>
                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                  Tag type <span style={{ color: "var(--status-danger)" }}>*</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={tagTypeId}
                  onChange={(e) => setTagTypeId(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">Select tag type…</option>
                  {tagTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: "1 1 220px" }}>
                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                  Tag <span style={{ color: "var(--status-danger)" }}>*</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={tagId}
                  onChange={(e) => setTagId(e.target.value ? Number(e.target.value) : "")}
                  disabled={!tagTypeId}
                >
                  <option value="">{tagTypeId ? "Select tag…" : "Pick tag type first"}</option>
                  {tagsForType.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <Form.Group className="mb-3" style={{ maxWidth: 180 }}>
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                Tag Date <span style={{ color: "var(--status-danger)" }}>*</span>
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={taggedDate}
                onChange={(e) => setTaggedDate(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional"
              />
            </Form.Group>

            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-strong)", marginBottom: 6 }}>
              Enrollments ({selectedEnrollmentIds.length} of {enrollments.length} selected)
            </div>

            {enrollments.length === 0 ? (
              <div
                style={{
                  padding: 16,
                  background: "#fefce8",
                  color: "#854d0e",
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                The selected students have no enrollments.
              </div>
            ) : (
              <div
                style={{
                  maxHeight: 280,
                  overflow: "auto",
                  border: "1px solid var(--border-default)",
                  borderRadius: 6,
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...headerCellStyle, width: 36, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          disabled={submitting}
                          style={{ margin: 0, cursor: "pointer" }}
                        />
                      </th>
                      <th style={headerCellStyle}>Enrollment</th>
                      <th style={headerCellStyle}>Student</th>
                      <th style={headerCellStyle}>Existing Tag</th>
                      <th style={headerCellStyle}>Last Tagged Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((e) => (
                      <tr key={e.enrollmentId}>
                        <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(e.enrollmentId)}
                            onChange={() => toggle(e.enrollmentId)}
                            disabled={submitting}
                            style={{ margin: 0, cursor: "pointer" }}
                          />
                        </td>
                        <td style={bodyCellStyle}>
                          <span style={{ fontWeight: 600 }}>#{e.enrollmentId}</span>
                          {e.studentCode && (
                            <span style={{ marginLeft: 6, color: "var(--text-muted)" }}>
                              {e.studentCode}
                            </span>
                          )}
                        </td>
                        <td style={bodyCellStyle}>
                          <span style={{ color: "var(--text-strong)" }}>{e.studentName}</span>
                          <span style={{ marginLeft: 6, color: "var(--text-faint)", fontSize: 11 }}>
                            Enq {e.enquiryId}
                          </span>
                        </td>
                        <td style={bodyCellStyle}>
                          <span style={{ color: "var(--text-body)" }}>
                            {existing[e.enrollmentId]?.tagName || "—"}
                          </span>
                        </td>
                        <td style={bodyCellStyle}>
                          <span style={{ color: "var(--text-body)" }}>
                            {existing[e.enrollmentId]?.taggedDate || "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.tagged ?? 0}</strong> tagged
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "tagged" && (
                          <span style={{ color: "var(--text-body)" }}>Tag assigned</span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_tagged"
                              ? "Already has this tag"
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                !tagTypeId ||
                !tagId ||
                !taggedDate ||
                selectedEnrollmentIds.length === 0
              }
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Updating…
                </>
              ) : (
                <>Update</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkAssignTagsModal;
